import React, { useEffect, useState } from "react";
import {
  Typography,
  Box,
  Tabs,
  Tab,
  Button,
  CircularProgress,
} from "@mui/material";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  BarChart,
  Bar,
  CartesianGrid,
  Legend,
} from "recharts";
import "./Analytics.css";

const COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042"];

function Analytics() {
  const [tab, setTab] = useState(0);
  const [overview, setOverview] = useState(null);
  const [loginStats, setLoginStats] = useState(null);
  const [topPosts, setTopPosts] = useState([]);
  const [scatterData, setScatterData] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const res1 = await fetch(`${process.env.REACT_APP_API_URL}/analytics`);
      const res2 = await fetch(
        `${process.env.REACT_APP_API_URL}/analytics/login-distribution`
      );
      const res3 = await fetch(
        `${process.env.REACT_APP_API_URL}/analytics/top-posts`
      );
      // Optional scatter data
      const res4 = await fetch(
        `${process.env.REACT_APP_API_URL}/analytics/post-views-by-day`
      );

      const [overviewData, loginData, topPostData, scatter] = await Promise.all(
        [res1.json(), res2.json(), res3.json(), res4.json()]
      );

      setOverview(overviewData);
      setLoginStats([
        { name: "Logged In", value: loginData.loggedIn },
        { name: "Guests", value: loginData.guests },
      ]);
      setTopPosts(topPostData);
      setScatterData(scatter);
    } catch (err) {
      console.error("Analytics fetch failed:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const handleDownload = () => {
    window.open(`${process.env.REACT_APP_API_URL}/analytics/export-users`);
  };

  const tabStyle = {
    minHeight: "48px",
    fontWeight: "bold",
    fontSize: "1rem",
  };

  if (loading) {
    return (
      <Box textAlign="center" mt={10}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box py={2} className="analytics-container">
      <Typography
        variant="h4"
        fontWeight="bold"
        color="black"
        textAlign="center"
        gutterBottom
      >
        WEBSITE ANALYTICS
      </Typography>

      {/* Tabs */}
      <Tabs
        value={tab}
        onChange={(e, newVal) => setTab(newVal)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ mb: 3, borderBottom: "1px solid #ccc" }}
      >
        <Tab label="Overview" sx={tabStyle} />
        <Tab label="User Types (Pie)" sx={tabStyle} />
        <Tab label="Post Views (Scatter)" sx={tabStyle} />
        <Tab label="Top Posts" sx={tabStyle} />
        <Tab label="Export Users" sx={tabStyle} />
      </Tabs>

      {/* Overview */}
      {tab === 0 && overview && (
        <Box className="analytics-grid">
          {[
            { label: "Total Users", value: overview.users, color: "#4CAF50" },
            { label: "Total Posts", value: overview.posts, color: "#2196F3" },
            {
              label: "Total Messages",
              value: overview.messages,
              color: "#FF9800",
            },
          ].map((item, index) => (
            <div
              key={index}
              className="analytics-card"
              style={{ backgroundColor: item.color }}
            >
              <div className="analytics-left">
                <div className="analytics-card-label">{item.label}</div>
              </div>
              <div className="analytics-card-value">{item.value}</div>
            </div>
          ))}
        </Box>
      )}

      {/* Pie Chart: User Types */}
      {tab === 1 && loginStats && (
        <Box className="chart-container">
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={loginStats}
                dataKey="value"
                nameKey="name"
                outerRadius={100}
                fill="#8884d8"
                label
              >
                {loginStats.map((_, index) => (
                  <Cell key={index} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </Box>
      )}

      {/* Scatter Chart: Post Views */}
      {tab === 2 && (
        <Box className="chart-container">
          <ResponsiveContainer width="100%" height={300}>
            <ScatterChart>
              <CartesianGrid />
              <XAxis dataKey="day" name="Day" />
              <YAxis dataKey="views" name="Views" />
              <Tooltip />
              <Scatter data={scatterData} fill="#8884d8" />
            </ScatterChart>
          </ResponsiveContainer>
        </Box>
      )}

      {/* Top Posts */}
      {tab === 3 && (
        <Box className="chart-container">
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={topPosts}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="title" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="count" fill="#1976d2" />
            </BarChart>
          </ResponsiveContainer>
        </Box>
      )}

      {/* Export Users */}
      {tab === 4 && (
        <Box textAlign="center" mt={4}>
          <Button variant="contained" onClick={handleDownload}>
            Download User List (Excel)
          </Button>
        </Box>
      )}
    </Box>
  );
}

export default Analytics;
